import React from 'react';
import { motion } from 'framer-motion';
import {
  FaReact, FaHtml5, FaCss3, FaJsSquare, FaNodeJs, FaPython, FaGitAlt, FaGithub,
  FaDocker, FaAws, FaSass,
} from 'react-icons/fa';
import {
  SiTailwindcss, SiNextdotjs, SiTypescript, SiMongodb, SiExpress, SiPostgresql,
} from 'react-icons/si';

const ALL_ICONS = [
  { name: 'React', icon: <FaReact />, color: '#61DBFB' },
  { name: 'Next.js', icon: <SiNextdotjs />, color: '#ffffff' },
  { name: 'TypeScript', icon: <SiTypescript />, color: '#3178C6' },
  { name: 'JavaScript', icon: <FaJsSquare />, color: '#F7DF1E' },
  { name: 'Tailwind', icon: <SiTailwindcss />, color: '#38B2AC' },
  { name: 'Html5', icon: <FaHtml5 />, color: '#E34F26' },
  { name: 'Css3', icon: <FaCss3 />, color: '#1572B6' },
  { name: 'Node.js', icon: <FaNodeJs />, color: '#339933' },
  { name: 'Express.js', icon: <SiExpress />, color: '#ffffff' },
  { name: 'MongoDB', icon: <SiMongodb />, color: '#47A248' },
  { name: 'PostgreSQL', icon: <SiPostgresql />, color: '#336791' },
  { name: 'Python', icon: <FaPython />, color: '#3776AB' },
  { name: 'Docker', icon: <FaDocker />, color: '#2496ED' },
  { name: 'AWS', icon: <FaAws />, color: '#FF9900' },
  { name: 'Git', icon: <FaGitAlt />, color: '#F05032' },
  { name: 'GitHub', icon: <FaGithub />, color: '#ffffff' },
  { name: 'Sass', icon: <FaSass />, color: '#CC6699' },
];

const CATEGORIES = [
  {
    title: 'Frontend',
    accent: 'from-mint-400 to-cyan-400',
    items: ['React', 'Next.js', 'TypeScript', 'Tailwind', 'Html5', 'Css3', 'Sass'],
  },
  {
    title: 'Backend',
    accent: 'from-indigo-400 to-cyan-300',
    items: ['Node.js', 'Express.js', 'Python'],
  },
  {
    title: 'Database',
    accent: 'from-cyan-400 to-mint-400',
    items: ['MongoDB', 'PostgreSQL'],
  },
  {
    title: 'Tooling & Cloud',
    accent: 'from-indigo-400 to-mint-400',
    items: ['Docker', 'AWS', 'Git', 'GitHub'],
  },
];

const iconByName = Object.fromEntries(ALL_ICONS.map((i) => [i.name, i]));

const Skills = () => {
  const marqueeIcons = [...ALL_ICONS, ...ALL_ICONS];

  return (
    <section id="skills" className="relative py-20 sm:py-28 bg-ink-950 overflow-hidden font-body">
      <div className="absolute inset-0 bg-grain pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="section-eyebrow">Skills</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold font-display">
            <span className="text-gradient">A full-stack toolbox</span>
          </h2>
        </motion.div>
      </div>

      {/* Infinite marquee of icons */}
      <div className="relative mb-14 overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-16 sm:w-32 bg-gradient-to-r from-ink-950 to-transparent z-10" />
        <div className="absolute inset-y-0 right-0 w-16 sm:w-32 bg-gradient-to-l from-ink-950 to-transparent z-10" />
        <div className="flex w-max gap-4 animate-marquee">
          {marqueeIcons.map((skill, i) => (
            <div
              key={`${skill.name}-${i}`}
              className="flex items-center gap-2 px-4 py-2.5 glass rounded-full shrink-0"
              title={skill.name}
            >
              <span className="text-xl" style={{ color: skill.color }}>
                {skill.icon}
              </span>
              <span className="text-sm text-white/60 font-mono whitespace-nowrap">
                {skill.name}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CATEGORIES.map((cat, ci) => (
            <motion.div
              key={cat.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: ci * 0.1 }}
              className="glass glass-hover rounded-2xl p-6"
            >
              <h3
                className={`text-sm font-semibold font-mono uppercase tracking-wider mb-5 bg-gradient-to-r ${cat.accent} bg-clip-text text-transparent`}
              >
                {cat.title}
              </h3>
              <div className="space-y-3">
                {cat.items.map((name) => {
                  const skill = iconByName[name];
                  if (!skill) return null;
                  return (
                    <div key={name} className="flex items-center gap-3 text-white/70">
                      <span className="text-lg shrink-0" style={{ color: skill.color }}>
                        {skill.icon}
                      </span>
                      <span className="text-sm">{name}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
